import {
  fingerprintPrivateSummary,
  parseTerms,
  validatePrivateSummaryTransition,
  type Bookmark,
} from "./domain";

export const MAX_PRIVATE_NOTES_LENGTH = 20_000;
export const MAX_PRIVATE_SUMMARY_LENGTH = 500;
export const MAX_PRIVATE_KEYWORDS = 12;
export const MAX_PRIVATE_KEYWORD_LENGTH = 80;

export interface PrivateQuickEditInput {
  privateNotes: string;
  privateHumanSummary: string;
  keywords: string[];
}

export type PrivateQuickEditField = "notes" | "summary" | "keywords";

export interface PrivateQuickEditError {
  field: PrivateQuickEditField;
  message: string;
}

export function normalizePrivateQuickEdit(
  input: PrivateQuickEditInput,
): PrivateQuickEditInput {
  return {
    privateNotes: input.privateNotes.trim(),
    privateHumanSummary: input.privateHumanSummary.trim(),
    keywords: parseTerms(input.keywords.join(",")),
  };
}

export function validatePrivateQuickEdit(
  bookmark: Bookmark,
  input: PrivateQuickEditInput,
): PrivateQuickEditError | null {
  if (input.privateNotes.length > MAX_PRIVATE_NOTES_LENGTH) {
    return {
      field: "notes",
      message: `Keep private Notes to ${MAX_PRIVATE_NOTES_LENGTH.toLocaleString(
        "en",
      )} characters or fewer.`,
    };
  }
  if (input.privateHumanSummary.length > MAX_PRIVATE_SUMMARY_LENGTH) {
    return {
      field: "summary",
      message: `Keep the private Summary to ${MAX_PRIVATE_SUMMARY_LENGTH} characters or fewer.`,
    };
  }
  const summaryError = validatePrivateSummaryTransition(
    bookmark.privateHumanSummary,
    input.privateHumanSummary,
  );
  if (summaryError) {
    return { field: "summary", message: summaryError };
  }
  const keywords = parseTerms(input.keywords.join(","));
  if (keywords.length > MAX_PRIVATE_KEYWORDS) {
    return {
      field: "keywords",
      message: `Use ${MAX_PRIVATE_KEYWORDS} private keywords or fewer.`,
    };
  }
  if (keywords.some((keyword) => keyword.length > MAX_PRIVATE_KEYWORD_LENGTH)) {
    return {
      field: "keywords",
      message: `Keep every private keyword to ${MAX_PRIVATE_KEYWORD_LENGTH} characters or fewer.`,
    };
  }
  return null;
}

export function applyPrivateQuickEdit(
  bookmark: Bookmark,
  input: PrivateQuickEditInput,
): Bookmark {
  const error = validatePrivateQuickEdit(bookmark, input);
  if (error) throw new Error(error.message);
  const normalized = normalizePrivateQuickEdit(input);
  const privateNotes = input.privateNotes === bookmark.privateNotes
    ? bookmark.privateNotes
    : normalized.privateNotes;
  const privateHumanSummary = (
    input.privateHumanSummary === bookmark.privateHumanSummary
  )
    ? bookmark.privateHumanSummary
    : normalized.privateHumanSummary;
  const summaryChanged = (
    privateHumanSummary !== bookmark.privateHumanSummary
  );
  return {
    ...bookmark,
    privateNotes,
    privateHumanSummary,
    privateSummaryInputMode: summaryChanged
      ? privateHumanSummary ? "typed" : null
      : bookmark.privateSummaryInputMode,
    summaryFingerprint: summaryChanged
      ? fingerprintPrivateSummary(privateHumanSummary)
      : bookmark.summaryFingerprint,
    embeddedSummaryFingerprint: summaryChanged
      ? null
      : bookmark.embeddedSummaryFingerprint,
    vectorStatus: summaryChanged ? "pending" : bookmark.vectorStatus,
    keywords: normalized.keywords,
  };
}

export function privateQuickEditChangesBookmark(
  bookmark: Bookmark,
  input: PrivateQuickEditInput,
): boolean {
  const updated = applyPrivateQuickEdit(bookmark, input);
  return (
    updated.privateNotes !== bookmark.privateNotes
    || updated.privateHumanSummary !== bookmark.privateHumanSummary
    || JSON.stringify(updated.keywords) !== JSON.stringify(bookmark.keywords)
  );
}
